import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { useAdmin } from './useAdmin';

interface DevModeState {
  isDevMode: boolean;
  isLoading: boolean;
  // Track the persisted value separately from effective value
  persistedValue: boolean;
}

export function useDevMode() {
  const { user } = useAuth();
  const { isAdmin, isMaster, isLoading: isAdminLoading } = useAdmin();
  const [state, setState] = useState<DevModeState>({
    isDevMode: false,
    isLoading: true,
    persistedValue: false,
  });

  // Fetch dev mode setting from profile
  const fetchDevMode = useCallback(async () => {
    // Wait for admin state to load first
    if (isAdminLoading) {
      return; // Don't do anything until we know the role
    }

    // Non-admin/master users: only show dev tools in actual DEV environment
    if (!user || (!isAdmin && !isMaster)) {
      setState({ 
        isDevMode: import.meta.env.DEV,
        isLoading: false,
        persistedValue: false,
      });
      return;
    }

    // Admin/master users: respect their explicit toggle preference
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('settings')
        .eq('id', user.id)
        .maybeSingle();

      if (error) throw error;

      const settings = data?.settings as Record<string, unknown> | null;
      const devModeEnabled = settings?.devModeEnabled === true;

      setState({ 
        // Admin/master: use their persisted preference
        isDevMode: devModeEnabled, 
        isLoading: false,
        persistedValue: devModeEnabled,
      });
    } catch (err) {
      console.error('[DevMode] Error fetching dev mode setting:', err);
      setState({ 
        isDevMode: false, 
        isLoading: false,
        persistedValue: false,
      });
    }
  }, [user, isAdmin, isMaster, isAdminLoading]);

  // Initial fetch
  useEffect(() => {
    fetchDevMode();
  }, [fetchDevMode]);

  // Listen for dev-mode-changed events for instant updates
  useEffect(() => {
    const handleDevModeChanged = (event: CustomEvent<{ enabled: boolean }>) => {
      setState(prev => ({ 
        ...prev, 
        // Admin/master: use the event value directly
        isDevMode: event.detail.enabled,
        persistedValue: event.detail.enabled,
      }));
    };

    window.addEventListener('dev-mode-changed', handleDevModeChanged as EventListener);
    return () => {
      window.removeEventListener('dev-mode-changed', handleDevModeChanged as EventListener);
    };
  }, []);

  const toggleDevMode = useCallback(async () => {
    if (!user) {
      console.log('[DevMode] No user, cannot toggle');
      return;
    }

    // Toggle the persisted value
    const newValue = !state.persistedValue;
    
    // Optimistic update - directly use the new value
    setState(prev => ({ 
      ...prev, 
      isDevMode: newValue,
      persistedValue: newValue,
    }));

    try {
      // Fetch current settings first
      const { data: profile, error: fetchError } = await supabase
        .from('profiles')
        .select('settings')
        .eq('id', user.id)
        .maybeSingle();

      if (fetchError) {
        console.error('[DevMode] Error fetching profile:', fetchError);
        // Revert on error
        setState(prev => ({ 
          ...prev, 
          isDevMode: !newValue,
          persistedValue: !newValue,
        }));
        return;
      }

      const currentSettings = (profile?.settings as Record<string, unknown>) || {};
      
      const { error: updateError } = await supabase
        .from('profiles')
        .update({
          settings: { ...currentSettings, devModeEnabled: newValue },
          updated_at: new Date().toISOString(),
        })
        .eq('id', user.id);

      if (updateError) {
        console.error('[DevMode] Error updating profile:', updateError);
        // Revert on error
        setState(prev => ({ 
          ...prev, 
          isDevMode: !newValue,
          persistedValue: !newValue,
        }));
        return;
      }

      console.log('[DevMode] Successfully toggled to:', newValue);
      
      // Dispatch event for other components to react
      window.dispatchEvent(new CustomEvent('dev-mode-changed', { 
        detail: { enabled: newValue } 
      }));
    } catch (err) {
      console.error('[DevMode] Error toggling dev mode:', err);
      // Revert on error
      setState(prev => ({ 
        ...prev, 
        isDevMode: !newValue,
        persistedValue: !newValue,
      }));
    }
  }, [user, state.persistedValue]);

  return {
    isDevMode: state.isDevMode,
    isLoading: state.isLoading,
    toggleDevMode,
    // The actual persisted value (useful for showing toggle state)
    persistedDevMode: state.persistedValue,
    // Convenience: can show dev panel?
    // Admin/master: use their persisted preference; non-admin: only in DEV environment
    canShowDevPanel: (isAdmin || isMaster) ? state.isDevMode : import.meta.env.DEV,
  };
}
